import { Spacing, Typography } from '@persona-id/design-tokens';
import { ArrowRightIcon, CircleInfoIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import CopyableField from 'dashboard/components/CopyableField';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import Permissioned from 'dashboard/components/Permissioned';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { FormCreateApiKey, FormCreateApiKeyData } from 'dashboard/forms/FormCreateApiKey';
import { FormExpireApiKey, FormExpireApiKeyData } from 'dashboard/forms/FormExpireApiKey';
import {
  FormPasswordConfirmationModal,
  FormPasswordConfirmationModalData,
} from 'dashboard/forms/FormPasswordConfirmationModal';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, {
  TableActionTD,
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { ApiKey } from 'dashboard/models/api-key';
import { Environment } from 'dashboard/models/environment';
import { Organization } from 'dashboard/models/organization';
import DashboardAPIAccessTable from 'dashboard/views/DashboardApiKeysView/DashboardAPIAccessTable';
import Banner from 'design-system/components/Banner';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import AutomationNullState from 'images/dashboard/automation-null-state.png';
import { ABBR_DATE_FORMAT } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

interface DashboardApiKeysViewProps {
  currentEnvironment: Environment;
  organization: Organization;
  apiKeys: ApiKey[];
  isLoadingApiKeys: boolean;
  handleCreateApiKey: (
    data: FormCreateApiKeyData,
    meta?: FormPasswordConfirmationModalData,
  ) => Promise<void>;
  handleShowApiKey: (apiKey: ApiKey) => void;
  handleExpireApiKey: (
    apiKey: ApiKey,
    data: FormExpireApiKeyData,
    meta?: FormPasswordConfirmationModalData,
  ) => Promise<void>;
}

const DashboardApiKeysView = ({
  currentEnvironment,
  organization,
  apiKeys,
  isLoadingApiKeys,
  handleCreateApiKey,
  handleShowApiKey,
  handleExpireApiKey,
}: DashboardApiKeysViewProps) => {
  // Do not require password confirmation if Organization has SAML enabled
  const requirePasswordConfirmation = !organization.attributes['has-saml-enabled'];
  const productFeatures = organization.attributes['product-features'];

  const [
    passwordConfirmationCallback,
    // NOTE: Because this is a function state, need to "wrap" the function call
    // i.e. () => () => {}
    //
    // Reference: https://stackoverflow.com/questions/55621212/is-it-possible-to-react-usestate-in-react
    setPasswordConfirmationCallback,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ] = useState<(meta: FormPasswordConfirmationModalData) => void>(null);

  const [
    isOpenPasswordConfirmationModal,
    { enable: showPasswordConfirmationModal, disable: hidePasswordConfirmationModal },
  ] = useToggle(false);

  const [
    isOpenCreateApiKeyModal,
    { enable: showCreateApiKeyModal, disable: hideCreateApiKeyModal },
  ] = useToggle(false);

  const location = useLocation();
  const hasApiKeyWritePermission = usePermission(Permission.ApiKeyWrite);
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openCreateApiKeyModalParam = queryParams.get('openCreateApiKeyModal');
    if (openCreateApiKeyModalParam === 'true' && hasApiKeyWritePermission) {
      showCreateApiKeyModal();
    }
  }, [location, hasApiKeyWritePermission]);

  const [selectedApiKey, setSelectedApiKey] = useState(null);

  const [
    isOpenExpireApiKeyModal,
    { enable: showExpireApiKeyModal, disable: hideExpireApiKeyModal },
  ] = useToggle(false);

  return (
    <>
      <InactiveEnvironmentBanner />
      <Container>
        <Header title='API Keys' data-test='api-keys__header'>
          <Permissioned fallback={null} permission={Permission.ApiKeyWrite}>
            <Button
              id='api-keys__button--create-api-key'
              name='api-keys__button--create-api-key'
              data-test='api-keys__button--create-api-key'
              width='auto'
              onClick={showCreateApiKeyModal}
              prefixIcon={<PlusIcon />}
            >
              Create API key
            </Button>
          </Permissioned>
        </Header>
        <Section>
          <Card>
            <Card.Body>
              <Card.Title>API Keys</Card.Title>
              <Paragraph color='primary'>
                <span>
                  You can make multiple API Keys with different scopes and permissions. Many
                  businesses use our APIs to retrieve information, to prefill fields for client-side
                  integration methods, and to integrate with Persona.
                </span>{' '}
                <LinkWithIcon
                  href='https://docs.withpersona.com/reference/introduction'
                  target='_blank'
                  rel='noreferrer'
                >
                  Get started by reading our API Reference <ArrowRightIcon size={14} />
                </LinkWithIcon>
              </Paragraph>
              <Spacer height={1} />
              <StyledBanner variant='warning'>
                <CircleInfoIcon />
                Please ensure that your API keys are securely protected. Unauthorized access to API
                keys by third parties may lead to unpermitted access to personal data stored within
                Persona.
              </StyledBanner>
              <Spacer height={1} />
              <TableLayout
                dataTest='api-keys__table'
                isLoading={isLoadingApiKeys}
                showHeader={true}
                isEmpty={apiKeys.length === 0}
                nullState={
                  isLoadingApiKeys ? (
                    <LoadingIndicator />
                  ) : (
                    <TableNullState>
                      <NullImage src={AutomationNullState} />
                      <div>Create and configure your first API key.</div>
                      <a
                        href='https://docs.withpersona.com/reference/introduction'
                        target='_blank'
                        rel='noreferrer'
                      >
                        Learn more here.
                      </a>
                    </TableNullState>
                  )
                }
                headerTR={
                  <>
                    <TableTH>Name</TableTH>
                    <TableTH>Value</TableTH>
                    <TableTH>Last used at</TableTH>
                    <TableTH>Created at</TableTH>
                    <Permissioned fallback={null} permission={Permission.ApiKeyWrite}>
                      <TableTH>{/* Context menu */}</TableTH>
                    </Permissioned>
                  </>
                }
              >
                {apiKeys.map((apiKey) => (
                  <TableTR data-test={`api-keys__table__row--${apiKey.id}`} key={apiKey.id}>
                    <TableTD>
                      {apiKey.attributes.name || '—'}
                      {apiKey.attributes['expires-at'] == null ? null : (
                        <DangerSmallParagraph size='xs' color='brand'>
                          Expires in{' '}
                          {DateTime.fromISO(apiKey.attributes['expires-at']).toRelative()}
                        </DangerSmallParagraph>
                      )}
                    </TableTD>
                    <TableTD>
                      <CopyableField
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        value={apiKey.attributes['value']}
                        type='password'
                        id={`api-key-fields__show--${apiKey.id}`}
                        data-test={`api-key-fields__show--${apiKey.id}`}
                      />
                    </TableTD>
                    <TableTD>
                      {apiKey.attributes['last-used-at'] == null
                        ? '—'
                        : DateTime.fromISO(apiKey.attributes['last-used-at']).toFormat(
                            ABBR_DATE_FORMAT,
                          )}
                    </TableTD>
                    <TableTD>
                      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                      {DateTime.fromISO(apiKey.attributes['created-at']).toFormat(ABBR_DATE_FORMAT)}
                    </TableTD>

                    <Permissioned fallback={null} permission={Permission.ApiKeyWrite}>
                      <TableActionTD>
                        <ContextMenu
                          data-test={`api-keys__table__row-more--${apiKey.id}`}
                          items={[
                            {
                              id: 'edit-api-key',
                              label: 'Edit',
                              onClick: () => {
                                handleShowApiKey(apiKey);
                              },
                              hidden: false,
                            },
                            {
                              id: 'expire-api-key',
                              label: 'Expire',
                              onClick: () => {
                                // apiKey.attributes['expires-at']
                                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                                setSelectedApiKey(apiKey);
                                showExpireApiKeyModal();
                              },
                              hidden: false,
                              variant: 'danger' as const,
                            },
                          ]}
                          renderItem={(item) => (
                            <ContextMenu.Item
                              key={item.id}
                              id={`api-keys__table__row-more-${item.id}--${apiKey.id}`}
                              data-test={`api-keys__table__row-more-${item.id}--${apiKey.id}`}
                              onClick={(e) => {
                                // Needed to avoid triggering row click below
                                e.stopPropagation();
                                e.preventDefault();
                                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                                item.onClick();
                              }}
                              variant={item.variant}
                            >
                              {item.label}
                            </ContextMenu.Item>
                          )}
                        />
                      </TableActionTD>
                    </Permissioned>
                  </TableTR>
                ))}
              </TableLayout>
            </Card.Body>
          </Card>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          {productFeatures.length > 0 && (
            <DashboardAPIAccessTable
              currentEnvironment={currentEnvironment}
              isLoading={isLoadingApiKeys}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              productFeatures={productFeatures}
            />
          )}
        </Section>

        <FormCreateApiKey
          isOpen={isOpenCreateApiKeyModal}
          handleSubmit={(data: FormCreateApiKeyData) => {
            if (requirePasswordConfirmation) {
              setPasswordConfirmationCallback(() => (meta: FormPasswordConfirmationModalData) => {
                handleCreateApiKey(data, meta);
              });
              showPasswordConfirmationModal();
            } else {
              handleCreateApiKey(data);
            }
          }}
          handleCloseModal={hideCreateApiKeyModal}
        />

        <FormExpireApiKey
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          apiKey={selectedApiKey}
          isOpen={isOpenExpireApiKeyModal}
          handleSubmit={(apiKey: ApiKey, data: FormExpireApiKeyData) => {
            if (requirePasswordConfirmation) {
              setPasswordConfirmationCallback(() => (meta: FormPasswordConfirmationModalData) => {
                handleExpireApiKey(apiKey, data, meta);
              });
              showPasswordConfirmationModal();
            } else {
              handleExpireApiKey(apiKey, data);
            }
          }}
          handleCloseModal={hideExpireApiKeyModal}
        />

        <FormPasswordConfirmationModal
          isOpen={isOpenPasswordConfirmationModal}
          handleSubmit={passwordConfirmationCallback}
          handleCloseModal={hidePasswordConfirmationModal}
        />
      </Container>
    </>
  );
};

const NullImage = styled('img')`
  max-width: 350px;
`;

const DangerSmallParagraph = styled(Paragraph).attrs(() => ({
  size: 'sm',
}))`
  color: ${({ theme }) => theme.color.variants.danger.default};
  font-weight: ${({ theme }) => theme.text.paragraph.sm.fontWeight};
`;

const LinkWithIcon = styled.a`
  display: inline-flex;
  align-items: center;
`;

const StyledBanner = styled(Banner)`
  ${Typography.TextMedium};
  padding: ${Spacing[4]};
`;

export default DashboardApiKeysView;
